import { CapabilityProtectedRoute } from '@joyful/ui/app'
import type { RouteObject } from 'react-router-dom'

export const StaticOrgScopedRoutes: Array<RouteObject> = [
  {
    lazy: async () => {
      const { AccountAnnouncementsRoute } = await import('./AnnouncementsRoute')
      return { Component: AccountAnnouncementsRoute }
    },
    path: 'announcements',
  },
  {
    lazy: async () => {
      const { AnalysisRoute } = await import('./AnalysisRoute')
      return { Component: AnalysisRoute }
    },
    path: 'analysis/:analysisId',
  },
  {
    children: [
      {
        index: true,
        lazy: async () => {
          const { DashboardRoute } = await import('./DashboardRoute')
          return { Component: DashboardRoute }
        },
      },
      {
        lazy: async () => {
          const { CustomerDashboardRoute } =
            await import('./CustomerDashboardRoute')
          return { Component: CustomerDashboardRoute }
        },
        path: 'customer',
      },
    ],
    path: 'dashboard',
  },
  {
    children: [
      {
        index: true,
        lazy: async () => {
          const { PatientsRoute } = await import('./PatientsRoute')
          return {
            Component: () => {
              return (
                <CapabilityProtectedRoute
                  component={PatientsRoute}
                  capability="encounter.list.view"
                />
              )
            },
          }
        },
      },
      {
        lazy: async () => {
          const { PatientRoute } = await import('./PatientRoute')
          return {
            Component: () => {
              return (
                <CapabilityProtectedRoute
                  component={PatientRoute}
                  capability="encounter.details.view"
                />
              )
            },
          }
        },
        path: ':patientId',
      },
    ],
    path: 'patients',
  },
  {
    children: [
      {
        index: true,
        lazy: async () => {
          const { ProvidersRoute } = await import('./ProvidersRoute')
          return {
            Component: () => {
              return (
                <CapabilityProtectedRoute
                  component={ProvidersRoute}
                  capability="encounter.list.view"
                  featureFlag="provider_enrollment"
                />
              )
            },
          }
        },
      },
      {
        lazy: async () => {
          const { ProviderRoute } = await import('./ProviderRoute')
          return {
            Component: () => {
              return (
                <CapabilityProtectedRoute
                  component={ProviderRoute}
                  capability="encounter.details.view"
                  featureFlag="provider_enrollment"
                />
              )
            },
          }
        },
        path: ':providerId',
      },
    ],
    path: 'providers',
  },
  {
    children: [
      {
        index: true,
        lazy: async () => {
          const { EncountersRoute } = await import('./EncountersRoute')
          return {
            Component: () => {
              return (
                <CapabilityProtectedRoute
                  component={EncountersRoute}
                  capability="encounter.list.view"
                />
              )
            },
          }
        },
      },
      {
        lazy: async () => {
          const { EncounterRoute } = await import('./EncounterRoute')
          return {
            Component: () => {
              return (
                <CapabilityProtectedRoute
                  component={EncounterRoute}
                  capability={['encounter.details.view', 'encounter.list.view']}
                />
              )
            },
          }
        },
        path: ':encounterId',
      },
    ],
    path: 'encounters',
  },
  {
    children: [
      {
        index: true,
        lazy: async () => {
          const { ProjectsRoute } = await import('./ProjectsRoute')
          return { Component: ProjectsRoute }
        },
      },
      {
        lazy: async () => {
          const { ProjectRoute } = await import('./ProjectRoute')
          return { Component: ProjectRoute }
        },
        path: ':projectId',
      },
    ],
    path: 'projects',
  },
  // automations routes removed — migrated to workspace-web (Wave 2B-1b)
  {
    children: [
      {
        index: true,
        lazy: async () => {
          const { MembersTeamRoute } = await import('./MembersTeamRoute')
          return { Component: MembersTeamRoute }
        },
      },
      {
        lazy: async () => {
          const { MembersInvitationsRoute } =
            await import('./MembersInvitationsRoute')
          return { Component: MembersInvitationsRoute }
        },
        path: 'invitations',
      },
    ],
    lazy: async () => {
      const { MembersRoute } = await import('./MembersRoute')
      return {
        Component: () => {
          return (
            <CapabilityProtectedRoute
              component={MembersRoute}
              capability="account.members.view"
            />
          )
        },
      }
    },
    path: 'members',
  },
  {
    children: [
      {
        index: true,
        lazy: async () => {
          const { RequestsRoute } = await import('./RequestsRoute')
          return { Component: RequestsRoute }
        },
      },
      {
        lazy: async () => {
          const { RequestRoute } = await import('./RequestRoute')
          return { Component: RequestRoute }
        },
        path: ':practiceRequestId',
      },
    ],
    path: 'requests',
  },
  {
    lazy: async () => {
      const { UncollectibleRoute } = await import('./UncollectibleRoute')
      return { Component: UncollectibleRoute }
    },
    path: 'uncollectible',
  },
  {
    lazy: async () => {
      const { ClaimRulesRoute } = await import('./ClaimRulesRoute')
      return { Component: ClaimRulesRoute }
    },
    path: 'claim-rules',
  },
  {
    children: [
      {
        index: true,
        lazy: async () => {
          const { ClaimScrubberRoute } = await import('./ClaimScrubberRoute')
          return { Component: ClaimScrubberRoute }
        },
      },
      {
        lazy: async () => {
          const { ClaimScrubberTimesheetRoute } =
            await import('./ClaimScrubberTimesheetRoute')
          return { Component: ClaimScrubberTimesheetRoute }
        },
        path: 'timesheets/:timebillingId',
      },
    ],
    path: 'scrubber',
  },
  {
    lazy: async () => {
      const { DropboxRoute } = await import('./DropboxRoute')
      return {
        Component: () => {
          return (
            <CapabilityProtectedRoute
              component={DropboxRoute}
              capability="dropbox.view"
            />
          )
        },
      }
    },
    path: 'dropbox',
  },
  {
    lazy: async () => {
      const { LibraryRoute } = await import('./LibraryRoute')
      return {
        Component: () => {
          return (
            <CapabilityProtectedRoute
              component={LibraryRoute}
              capability="analysis.library.view"
              featureFlag="practice_library"
            />
          )
        },
      }
    },
    path: 'library/:analysisId?',
  },
  {
    lazy: async () => {
      const { FaxesMovedToBpoRoute } = await import('./FaxesMovedToBpoRoute')
      return { Component: FaxesMovedToBpoRoute }
    },
    path: 'faxes',
  },
]
